import {
  ApiError,
  ApiErrorInitialState,
} from '@hellodarwin/core/lib/features/entities';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../../../app/app-store';
import ClientApiV2 from '../client-api-v2';
import type {
  ClientTask,
  ClientTaskCounts,
  ClientTasksQuery,
  ClientTasksResponse,
} from '../client-task-types';

type RequestStatus = 'idle' | 'pending' | 'failed';

export interface ClientTasksState {
  tasks: ClientTask[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  counts: ClientTaskCounts;
  listStatus: RequestStatus;
  listError: ApiError;
  latestListRequestId?: string;
  requestedListQueryKey?: string;
  fulfilledListQueryKey?: string;
  focusedTask?: ClientTask;
  focusedTaskStatus: RequestStatus;
  latestFocusedTaskRequestId?: string;
  focusedTaskStartedMutationVersion?: number;
  mutationStatusById: Record<string, RequestStatus>;
  latestMutationRequestIdById: Record<string, string>;
  mutationVersion: number;
  taskMutationVersionById: Record<string, number>;
  listStartedMutationVersion?: number;
}

const emptyCounts: ClientTaskCounts = {
  all: 0,
  open: 0,
  completed: 0,
  completedLast30: 0,
};

export const initialClientTasksState: ClientTasksState = {
  tasks: [],
  total: 0,
  limit: 10,
  offset: 0,
  hasMore: false,
  counts: emptyCounts,
  listStatus: 'idle',
  listError: ApiErrorInitialState,
  focusedTaskStatus: 'idle',
  mutationStatusById: {},
  latestMutationRequestIdById: {},
  mutationVersion: 0,
  taskMutationVersionById: {},
};

const apiError = (error: any): ApiError =>
  error?.response?.data ?? ApiErrorInitialState;

export const clientTasksListQueryKey = (
  query: ClientTasksQuery,
  allPages = false,
) =>
  [
    query.programId ?? '',
    query.contactId ?? '',
    query.status ?? 'open',
    query.completedFilter ?? 'last30',
    query.page ?? 1,
    query.limit ?? 10,
    query.locale,
    allPages ? 'all-pages' : 'single-page',
  ].join('|');

export const fetchClientTasks = createAsyncThunk<
  ClientTasksResponse,
  { api: ClientApiV2; query: ClientTasksQuery; allPages?: boolean },
  { rejectValue: ApiError }
>('clientTasks/fetchList', async ({ api, query, allPages }, { rejectWithValue }) => {
  try {
    if (!allPages) {
      const { data } = await api.get<ClientTasksResponse>('/tasks', {
        params: query,
      });
      return data;
    }

    const tasksById = new Map<string, ClientTask>();
    let page = 1;
    let response: ClientTasksResponse | undefined;

    do {
      const { data } = await api.get<ClientTasksResponse>('/tasks', {
        params: { ...query, page },
      });
      const previousSize = tasksById.size;
      for (const task of data.tasks ?? []) {
        tasksById.set(task.task_id, task);
      }
      if (data.has_more && tasksById.size === previousSize) {
        throw new Error('Client task pagination did not advance');
      }
      const reportedPages = Math.max(
        1,
        Math.ceil(data.total / Math.max(1, data.limit)),
      );
      if (data.has_more && page >= reportedPages) {
        throw new Error('Client task pagination exceeded the reported total');
      }
      response = data;
      page += 1;
    } while (response.has_more);

    const tasks = Array.from(tasksById.values());
    return {
      ...response,
      tasks,
      total: tasks.length,
      offset: 0,
      has_more: false,
    };
  } catch (error: any) {
    return rejectWithValue(apiError(error));
  }
});

export const fetchClientTask = createAsyncThunk<
  ClientTask,
  { api: ClientApiV2; taskId: string; locale: string },
  { rejectValue: ApiError }
>(
  'clientTasks/fetchOne',
  async ({ api, taskId, locale }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<ClientTask>(`/tasks/${taskId}`, {
        params: { locale },
      });
      return data;
    } catch (error: any) {
      return rejectWithValue(apiError(error));
    }
  },
);

type ClientTaskMutationArgs = {
  api: ClientApiV2;
  taskId: string;
  locale: string;
};

const createTaskMutation = (action: 'complete' | 'reopen') =>
  createAsyncThunk<
    ClientTask,
    ClientTaskMutationArgs,
    { rejectValue: ApiError }
  >(
    `clientTasks/${action}`,
    async ({ api, taskId, locale }, { rejectWithValue }) => {
      try {
        const { data } = await api.post<ClientTask>(
          `/tasks/${taskId}/${action}`,
          undefined,
          { params: { locale } },
        );
        return data;
      } catch (error: any) {
        return rejectWithValue(apiError(error));
      }
    },
  );

export const completeClientTask = createTaskMutation('complete');
export const reopenClientTask = createTaskMutation('reopen');

const replaceTask = (state: ClientTasksState, task: ClientTask) => {
  const index = state.tasks.findIndex(
    ({ task_id }) => task_id === task.task_id,
  );
  if (index >= 0) {
    state.tasks[index] = task;
  }
  if (state.focusedTask?.task_id === task.task_id) {
    state.focusedTask = task;
  }
};

const clientTasksSlice = createSlice({
  name: 'clientTasks',
  initialState: initialClientTasksState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchClientTasks.pending, (state, action) => {
      const requestedQueryKey = clientTasksListQueryKey(
        action.meta.arg.query,
        action.meta.arg.allPages,
      );
      if (state.fulfilledListQueryKey !== requestedQueryKey) {
        state.tasks = [];
        state.total = 0;
        state.offset = 0;
        state.hasMore = false;
        state.counts = emptyCounts;
      }
      state.listStatus = 'pending';
      state.listError = ApiErrorInitialState;
      state.latestListRequestId = action.meta.requestId;
      state.requestedListQueryKey = requestedQueryKey;
      state.listStartedMutationVersion = state.mutationVersion;
    });
    builder.addCase(fetchClientTasks.fulfilled, (state, action) => {
      if (state.latestListRequestId !== action.meta.requestId) return;

      const mutationVersionAtRequest = state.listStartedMutationVersion ?? 0;
      state.tasks = (action.payload.tasks ?? []).map((task) => {
        if (
          (state.taskMutationVersionById[task.task_id] ?? 0) <=
          mutationVersionAtRequest
        ) {
          return task;
        }

        return (
          state.tasks.find(({ task_id }) => task_id === task.task_id) ??
          (state.focusedTask?.task_id === task.task_id
            ? state.focusedTask
            : task)
        );
      });
      state.total = action.payload.total;
      state.limit = action.payload.limit;
      state.offset = action.payload.offset;
      state.hasMore = action.payload.has_more;
      state.counts = action.payload.counts ?? emptyCounts;
      state.listStatus = 'idle';
      state.fulfilledListQueryKey = clientTasksListQueryKey(
        action.meta.arg.query,
        action.meta.arg.allPages,
      );
      state.latestListRequestId = undefined;
      state.listStartedMutationVersion = undefined;
    });
    builder.addCase(fetchClientTasks.rejected, (state, action) => {
      if (state.latestListRequestId !== action.meta.requestId) return;

      state.listStatus = 'failed';
      state.listError = action.payload ?? ApiErrorInitialState;
      state.latestListRequestId = undefined;
      state.listStartedMutationVersion = undefined;
    });

    builder.addCase(fetchClientTask.pending, (state, action) => {
      state.focusedTaskStatus = 'pending';
      state.latestFocusedTaskRequestId = action.meta.requestId;
      state.focusedTaskStartedMutationVersion = state.mutationVersion;
    });
    builder.addCase(fetchClientTask.fulfilled, (state, action) => {
      if (state.latestFocusedTaskRequestId !== action.meta.requestId) return;

      const mutationVersionAtRequest =
        state.focusedTaskStartedMutationVersion ?? 0;
      const locallyUpdatedTask =
        state.focusedTask?.task_id === action.payload.task_id
          ? state.focusedTask
          : state.tasks.find(
              ({ task_id }) => task_id === action.payload.task_id,
            );
      state.focusedTask =
        (state.taskMutationVersionById[action.payload.task_id] ?? 0) >
          mutationVersionAtRequest && locallyUpdatedTask
          ? locallyUpdatedTask
          : action.payload;
      state.focusedTaskStatus = 'idle';
      state.latestFocusedTaskRequestId = undefined;
      state.focusedTaskStartedMutationVersion = undefined;
    });
    builder.addCase(fetchClientTask.rejected, (state, action) => {
      if (state.latestFocusedTaskRequestId !== action.meta.requestId) return;

      state.focusedTask = undefined;
      state.focusedTaskStatus = 'failed';
      state.latestFocusedTaskRequestId = undefined;
      state.focusedTaskStartedMutationVersion = undefined;
    });

    for (const mutation of [completeClientTask, reopenClientTask]) {
      builder.addCase(mutation.pending, (state, action) => {
        state.mutationStatusById[action.meta.arg.taskId] = 'pending';
        state.latestMutationRequestIdById[action.meta.arg.taskId] =
          action.meta.requestId;
      });
      builder.addCase(mutation.fulfilled, (state, action) => {
        if (
          state.latestMutationRequestIdById[action.meta.arg.taskId] !==
          action.meta.requestId
        ) {
          return;
        }

        state.mutationVersion += 1;
        state.taskMutationVersionById[action.payload.task_id] =
          state.mutationVersion;
        state.mutationStatusById[action.payload.task_id] = 'idle';
        delete state.latestMutationRequestIdById[action.payload.task_id];
        replaceTask(state, action.payload);
      });
      builder.addCase(mutation.rejected, (state, action) => {
        if (
          state.latestMutationRequestIdById[action.meta.arg.taskId] !==
          action.meta.requestId
        ) {
          return;
        }

        state.mutationStatusById[action.meta.arg.taskId] = 'failed';
        delete state.latestMutationRequestIdById[action.meta.arg.taskId];
      });
    }
  },
});

export const selectClientTasks = (state: RootState) => state.clientTasks.tasks;
export const selectClientTasksTotal = (state: RootState) =>
  state.clientTasks.total;
export const selectClientTaskCounts = (state: RootState) =>
  state.clientTasks.counts;
export const selectClientTasksListStatus = (state: RootState) =>
  state.clientTasks.listStatus;
export const selectClientTasksListError = (state: RootState) =>
  state.clientTasks.listError;
export const selectClientTasksFulfilledListQueryKey = (state: RootState) =>
  state.clientTasks.fulfilledListQueryKey;
export const selectClientTasksRequestedListQueryKey = (state: RootState) =>
  state.clientTasks.requestedListQueryKey;
export const selectFocusedClientTask = (state: RootState) =>
  state.clientTasks.focusedTask;
export const selectFocusedClientTaskStatus = (state: RootState) =>
  state.clientTasks.focusedTaskStatus;
export const selectClientTaskMutationStatus = (
  state: RootState,
  taskId: string,
) => state.clientTasks.mutationStatusById[taskId] ?? 'idle';
export const selectClientTaskMutationStatuses = (state: RootState) =>
  state.clientTasks.mutationStatusById;

export const clientTasksReducer = clientTasksSlice.reducer;
